import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { FileText, TrendingUp, Printer, PieChart as PieIcon, X } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const PERIODS = [
  { key: "day", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "halfyear", label: "6 Months" },
  { key: "year", label: "This Year" },
  { key: "lifetime", label: "Lifetime" },
];

/**
 * Sales by Person — who sold how much, over any period.
 * A salesperson sees only their own numbers; an admin sees their team;
 * Major/Super Admin see everyone. The backend enforces the same scoping.
 */
export function SalesByPersonPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get(`/api/crm/sales-by-person?period=${period}`).then((r) => setData(r.data)).catch(() => {});
  }, [period]);

  useEffect(() => {
    api.get("/api/crm/sales-by-person/me").then((r) => setMine(r.data)).catch(() => {});
  }, []);

  const t = data?.totals || {};

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Performance</div>
        <h1 className="font-display text-4xl text-maroon">Sales by Person</h1>
        <p className="text-xs text-ash mt-1">
          {data?.scope === "all"
            ? "Every seller across both showrooms."
            : "You and the people reporting to you."}
        </p>
      </div>

      {/* My own numbers, every period at a glance */}
      {mine && (
        <div className="bg-white border border-line p-5 mb-6" data-testid="my-sales">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-gold"/>
            <h2 className="font-display text-2xl text-maroon">My Sales — {mine.name}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            {PERIODS.map((p) => (
              <div key={p.key} className="border border-line p-3" data-testid={`my-${p.key}`}>
                <div className="eyebrow text-[9px]">{p.label}</div>
                <div className="font-display text-xl text-maroon mt-1">{fmtINR(mine.by_period?.[p.key]?.revenue || 0)}</div>
                <div className="text-[10px] text-ash">
                  {mine.by_period?.[p.key]?.orders_count || 0} orders · {mine.by_period?.[p.key]?.items_sold || 0} items
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Period switcher */}
      <div className="flex flex-wrap gap-2 mb-4">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${period === p.key ? "bg-maroon text-pearl border-maroon" : "border-line text-ash hover:border-maroon"}`}
            data-testid={`period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Team totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Revenue" value={fmtINR(t.revenue || 0)}/>
        <Stat label="Orders" value={t.orders_count || 0}/>
        <Stat label="Items Sold" value={t.items_sold || 0}/>
        <Stat label="Slips Billed" value={t.slips_count || 0}/>
      </div>

      {/* Per-person table */}
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Person", "Role", "Revenue", "Orders", "Items", "Slips", ""].map((h) => (
              <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(data?.people || []).map((p) => (
              <React.Fragment key={p.staff_id}>
                <tr className="border-b border-line/60" data-testid={`sales-${p.staff_id}`}>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-xs text-ash">{(p.role || "").replace("_", " ")}</td>
                  <td className="p-3 text-maroon font-medium">{fmtINR(p.revenue)}</td>
                  <td className="p-3">{p.orders_count}</td>
                  <td className="p-3">{p.items_sold}</td>
                  <td className="p-3">{p.slips_count}</td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => setExpanded(expanded === p.staff_id ? null : p.staff_id)}
                      className="text-xs text-maroon underline"
                      data-testid={`expand-${p.staff_id}`}
                    >
                      {expanded === p.staff_id ? "Hide items" : "View items"}
                    </button>
                  </td>
                </tr>
                {expanded === p.staff_id && (
                  <tr className="bg-pearl/40">
                    <td colSpan={7} className="p-4">
                      {(p.items || []).length === 0 ? (
                        <div className="text-xs text-ash">No item detail for this period.</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead><tr className="text-ash">
                            {["Item", "Product ID", "Qty", "Amount", "When"].map((h) => (
                              <th key={h} className="text-left py-1 eyebrow text-[9px]">{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {p.items.map((it, i) => (
                              <tr key={i} className="border-t border-line/40">
                                <td className="py-1.5">{it.name}</td>
                                <td className="py-1.5 text-ash">{(it.product_id || "").slice(0, 8).toUpperCase()}</td>
                                <td className="py-1.5">{it.qty}</td>
                                <td className="py-1.5">{fmtINR(it.subtotal || 0)}</td>
                                <td className="py-1.5 text-ash">{it.at ? new Date(it.at).toLocaleDateString("en-GB") : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {(data?.people || []).length === 0 && (
          <div className="p-12 text-center text-ash text-sm">No sales recorded for this period.</div>
        )}
      </div>

      {/* Counter sales — the full slip-to-bill picture */}
      <SlipToBillSection period={period} />
    </div>
  );
}

const PIE_COLOURS = ["#6B1A2C", "#C9A063", "#8C6239", "#A63D5C", "#D4B483", "#5A3E2B", "#B08D57"];

/**
 * Slip to Bill — every counter sale, who made it and what went out.
 *
 * Sits under Sales by Person so one screen answers both "how much did each
 * person sell" and "what exactly did they hand over the counter".
 */
function SlipToBillSection({ period }) {
  const [data, setData] = useState(null);
  const [who, setWho] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [sortBy, setSortBy] = useState("date");

  useEffect(() => {
    const q = who ? `&staff_id=${who}` : "";
    api.get(`/api/crm/slips/analytics?period=${period}${q}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [period, who]);

  const t = data?.totals || {};
  const people = data?.people || [];

  const pieData = people.map((p) => ({
    name: p.name || "—",
    value: p.slips,
    items: p.items,
    customers: p.customers,
    avg: p.avg_items_per_slip,
  }));

  const slips = [...(data?.slips || [])].sort((a, b) => {
    if (sortBy === "person") return (a.salesperson_name || "").localeCompare(b.salesperson_name || "");
    if (sortBy === "items") return b.item_count - a.item_count;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="eyebrow">Counter sales</div>
          <h2 className="font-display text-3xl text-maroon">Slip to Bill</h2>
        </div>
        <select className="border border-line px-3 py-2 bg-pearl text-sm" value={who}
          onChange={(e) => setWho(e.target.value)} data-testid="slip-person-filter">
          <option value="">Everyone</option>
          {people.map((p) => <option key={p.staff_id} value={p.staff_id}>{p.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Slips" value={t.slips || 0}/>
        <Stat label="Items Handed Over" value={t.items || 0}/>
        <Stat label="Customers Served" value={t.customers || 0}/>
        <Stat label="Gold (22K) g" value={(t.grams?.["22K"] || 0).toFixed(2)}/>
      </div>

      {/* Grams by purity */}
      <div className="bg-white border border-line p-5 mb-5">
        <div className="eyebrow text-[10px] mb-3">Metal handed over — by purity</div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
          {["24K", "22K", "20K", "18K", "silver", "other"].map((k) => (
            <div key={k} className="border border-line p-3" data-testid={`slip-grams-${k}`}>
              <div className="eyebrow text-[9px]">{k === "silver" ? "Silver" : k === "other" ? "Other" : k}</div>
              <div className="font-display text-xl text-maroon mt-1">{(t.grams?.[k] || 0).toFixed(3)}</div>
              <div className="text-[10px] text-ash">grams</div>
            </div>
          ))}
        </div>
      </div>

      {/* Share of slips per person */}
      {pieData.length > 0 && (
        <div className="bg-white border border-line p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <PieIcon size={16} className="text-gold"/>
            <h3 className="font-display text-2xl text-maroon">Share of Slips</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLOURS[i % PIE_COLOURS.length]}/>)}
                </Pie>
                <Legend/>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border border-line p-3 text-xs shadow-lg">
                      <div className="font-medium text-maroon">{d.name}</div>
                      <div>{d.value} slips · {d.items} items</div>
                      <div>{d.customers} customers served</div>
                      <div>Avg {d.avg} items per slip</div>
                    </div>
                  );
                }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Per-person summary */}
      <div className="bg-white border border-line overflow-x-auto mb-5">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Person", "Role", "Slips", "Items", "Customers", "Avg Items/Slip", "Customers/Day",
                  "24K g", "22K g", "20K g", "18K g", "Silver g"].map((h) => (
              <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.staff_id} className="border-b border-line/60" data-testid={`slip-person-${p.staff_id}`}>
                <td className="p-3 font-medium whitespace-nowrap">{p.name}</td>
                <td className="p-3 text-xs text-ash whitespace-nowrap">{(p.role || "").replace("_", " ")}</td>
                <td className="p-3">{p.slips}</td>
                <td className="p-3">{p.items}</td>
                <td className="p-3">{p.customers}</td>
                <td className="p-3">{p.avg_items_per_slip}</td>
                <td className="p-3">{p.avg_customers_per_day}</td>
                {["24K", "22K", "20K", "18K", "silver"].map((k) => (
                  <td key={k} className="p-3 text-xs">{(p.grams?.[k] || 0).toFixed(3)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {people.length === 0 && <div className="p-10 text-center text-ash text-sm">No slips in this period.</div>}
      </div>

      {/* Every slip, expandable to the item detail */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] text-ash uppercase tracking-widest">Sort by</span>
        {[["date", "Newest"], ["person", "Person"], ["items", "Most items"]].map(([k, l]) => (
          <button key={k} onClick={() => setSortBy(k)}
            className={`px-2 py-1 text-[11px] border ${sortBy === k ? "bg-maroon text-pearl border-maroon" : "border-line text-ash hover:border-maroon"}`}
            data-testid={`slip-sort-${k}`}>{l}</button>
        ))}
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Slip No", "When", "By", "Role", "Customer", "Items", ""].map((h) => (
              <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {slips.map((s) => (
              <React.Fragment key={s.id}>
                <tr className="border-b border-line/60" data-testid={`slip-row-${s.id}`}>
                  <td className="p-3 font-mono text-xs">
                    {s.slip_no}
                    {s.edited && <span className="ml-1 text-[8px] uppercase tracking-widest bg-amber-500 text-white px-1 py-0.5 rounded">Edited</span>}
                  </td>
                  <td className="p-3 text-xs whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="p-3">{s.salesperson_name}</td>
                  <td className="p-3 text-xs text-ash">{(s.salesperson_role || "").replace("_", " ")}</td>
                  <td className="p-3">
                    <div>{s.customer_name || "—"}</div>
                    <div className="text-[10px] text-ash">{s.customer_phone}</div>
                  </td>
                  <td className="p-3">{s.item_count}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                      className="text-xs text-maroon underline" data-testid={`slip-expand-${s.id}`}>
                      {expanded === s.id ? "Hide" : "Items"}
                    </button>
                  </td>
                </tr>
                {expanded === s.id && (
                  <tr className="bg-pearl/40">
                    <td colSpan={7} className="p-4">
                      <table className="w-full text-xs">
                        <thead><tr className="text-ash">
                          {["", "Item", "Barcode", "HUID", "Purity", "Weight", "Qty"].map((h) => (
                            <th key={h} className="text-left py-1 eyebrow text-[9px]">{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {s.items.map((it, i) => (
                            <tr key={i} className="border-t border-line/40">
                              <td className="py-1.5">
                                {it.image
                                  ? <img src={it.image} alt="" className="w-10 h-10 object-cover border border-line"/>
                                  : <div className="w-10 h-10 bg-white border border-line"/>}
                              </td>
                              <td className="py-1.5">{it.name}</td>
                              <td className="py-1.5 font-mono">{it.barcode || "—"}</td>
                              <td className="py-1.5">{it.huid || "—"}</td>
                              <td className="py-1.5">{it.purity || "—"}</td>
                              <td className="py-1.5">{it.weight_grams != null ? `${it.weight_grams}g` : "—"}</td>
                              <td className="py-1.5">{it.qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {slips.length === 0 && <div className="p-10 text-center text-ash text-sm">No slips in this period.</div>}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white border border-line p-4">
    <div className="eyebrow text-[9px]">{label}</div>
    <div className="font-display text-2xl text-maroon mt-1">{value}</div>
  </div>
);

/**
 * Slip to Bill — the mini bills handed to the billing counter.
 * Salespeople see their own; admins and above see everyone's.
 */
export function SlipsPage() {
  const { user } = useAuth();
  const canEdit = hasRole(user, ["super_admin", "major_admin", "admin"]);
  const [slips, setSlips] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editSlip, setEditSlip] = useState(null); // slip currently being edited

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/api/crm/slip-to-bill?${params}`).then((r) => setSlips(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status]);

  const openPdf = async (slip) => {
    try {
      const resp = await api.get(`/api/crm/slip-to-bill/${slip.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open the slip"); }
  };

  const setSlipStatus = async (slip, newStatus) => {
    try {
      await api.post(`/api/crm/slip-to-bill/${slip.id}/status`, { status: newStatus });
      toast.success(`Slip ${slip.slip_no} marked ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteSlip = async (slip) => {
    if (!window.confirm(`Delete slip ${slip.slip_no}? The customer bought nothing from it. This cannot be undone.`)) return;
    try {
      await api.delete(`/api/crm/slip-to-bill/${slip.id}`);
      toast.success(`Slip ${slip.slip_no} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Billing</div>
        <h1 className="font-display text-4xl text-maroon">Slip to Bill</h1>
        <p className="text-xs text-ash mt-1">
          Mini bills handed to the billing counter. Print, then mark billed.
          {canEdit && " Admins can edit a slip if the customer drops a few pieces, or delete it if they buy nothing."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          className="border border-line px-3 py-2 bg-pearl text-sm"
          placeholder="Search slip no, customer, phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="slip-search"
        />
        <select className="border border-line px-3 py-2 bg-pearl text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="slip-status-filter">
          <option value="">All statuses</option>
          <option value="pending_billing">Pending Billing</option>
          <option value="billed">Billed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={load} className="btn-outline text-sm">Search</button>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Slip No", "When", "Customer", "Phone", "Items", "Total", "Salesperson", "Status", "Actions"].map((h) => (
              <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {slips.map((s) => (
              <tr key={s.id} className={`border-b border-line/60 ${s.edited ? "bg-amber-50" : ""}`} data-testid={`slip-${s.id}`}>
                <td className="p-3 font-mono text-xs">
                  {s.slip_no}
                  {s.edited && (
                    <span className="ml-1 align-middle text-[8px] uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded" title={`Edited by ${s.edited_by || "admin"}${s.edited_at ? " on " + new Date(s.edited_at).toLocaleString() : ""}`} data-testid={`slip-edited-${s.id}`}>
                      Edited
                    </span>
                  )}
                </td>
                <td className="p-3 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                <td className="p-3">{s.customer_name || "—"}</td>
                <td className="p-3 text-xs">{s.customer_phone}</td>
                <td className="p-3 text-xs">
                  {(s.items || []).length}
                  {s.edited && s.original_item_count != null && s.original_item_count !== (s.items || []).length && (
                    <span className="text-[10px] text-amber-700"> (was {s.original_item_count})</span>
                  )}
                </td>
                <td className="p-3 text-xs whitespace-nowrap">
                  {s.items_total ? <b className="text-maroon">{fmtINR(s.items_total)}</b> : "—"}
                  {s.huid_total > 0 && <div className="text-[9px] text-ash">incl. HUID {fmtINR(s.huid_total)}</div>}
                </td>
                <td className="p-3 text-xs">{s.salesperson_name}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                    s.status === "billed" ? "bg-green-100 text-green-800"
                    : s.status === "cancelled" ? "bg-red-100 text-red-700"
                    : "bg-gold/25 text-maroon"}`}>
                    {(s.status || "").replace("_", " ")}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => openPdf(s)} className="text-xs text-maroon underline inline-flex items-center gap-1" data-testid={`slip-print-${s.id}`}>
                      <Printer size={12}/> Print
                    </button>
                    {canEdit && (
                      <button onClick={() => setEditSlip(s)} className="text-xs text-amber-700 underline" data-testid={`slip-edit-${s.id}`}>Edit</button>
                    )}
                    {canEdit && (
                      <button onClick={() => deleteSlip(s)} className="text-xs text-red-600 underline" data-testid={`slip-delete-${s.id}`}>Delete</button>
                    )}
                    {s.status === "pending_billing" && (
                      <>
                        <button onClick={() => setSlipStatus(s, "billed")} className="text-xs text-green-700 underline" data-testid={`slip-bill-${s.id}`}>Mark Billed</button>
                        <button onClick={() => setSlipStatus(s, "cancelled")} className="text-xs text-red-600 underline">Cancel</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {slips.length === 0 && (
          <div className="p-12 text-center text-ash text-sm">
            <FileText size={20} className="mx-auto mb-2 opacity-40"/>
            No slips yet. Generate one from the cart while assisting a customer.
          </div>
        )}
      </div>

      {editSlip && (
        <EditSlipModal
          slip={editSlip}
          onClose={() => setEditSlip(null)}
          onSaved={() => { setEditSlip(null); load(); }}
          onDeleted={() => { setEditSlip(null); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Edit a slip — untick the pieces the customer decided against. Saving keeps
 * only the ticked items and flags the slip as edited. Unticking everything
 * points the admin at Delete instead.
 */
function EditSlipModal({ slip, onClose, onSaved, onDeleted }) {
  const [keep, setKeep] = useState(new Set((slip.items || []).map((it) => it.product_id)));
  const [notes, setNotes] = useState(slip.notes || "");
  const [busy, setBusy] = useState(false);
  // Hallmarking charge per piece, editable here. Rs 1250 is the standard; the
  // desk can raise or lower it and the item's price moves with it.
  const [huid, setHuid] = useState(() => {
    const init = {};
    (slip.items || []).forEach((it) => {
      if (it.huid_applicable !== false && (it.huid_charges != null || it.metal === "Gold")) {
        init[it.product_id] = it.huid_charges ?? 1250;
      }
    });
    return init;
  });

  const toggle = (pid) => setKeep((s) => {
    const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n;
  });

  const setHuidFor = (pid, raw) => {
    const v = raw.replace(/[^\d.]/g, "");
    setHuid((h) => ({ ...h, [pid]: v }));
  };

  // Live preview of what each line becomes, so the desk sees the effect of a
  // changed hallmarking charge before saving.
  const previewPrice = (it) => {
    const original = Number(it.price || 0);
    if (it.huid_applicable === false) return original;
    const was = Number(it.huid_charges ?? 0);
    const now = Number(huid[it.product_id]);
    if (!Number.isFinite(now)) return original;
    // The charge sits inside the taxed subtotal, so a change carries GST with it.
    const gstMult = 1 + (it.price_breakdown?.gst_percent ?? 3) / 100;
    return Math.round((original + (now - was) * gstMult) * 100) / 100;
  };

  const keptItems = (slip.items || []).filter((it) => keep.has(it.product_id));
  const newTotal = keptItems.reduce((a, it) => a + previewPrice(it), 0);
  const oldTotal = keptItems.reduce((a, it) => a + Number(it.price || 0), 0);

  const save = async () => {
    if (keep.size === 0) {
      toast.error("No items left — use Delete to remove the whole slip instead");
      return;
    }
    setBusy(true);
    try {
      const charges = {};
      Object.entries(huid).forEach(([pid, v]) => {
        const n = Number(v);
        if (Number.isFinite(n) && n >= 0) charges[pid] = n;
      });
      await api.patch(`/api/crm/slip-to-bill/${slip.id}`, {
        keep_product_ids: Array.from(keep),
        huid_charges: charges,
        notes,
      });
      toast.success(`Slip ${slip.slip_no} updated`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the slip"); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete slip ${slip.slip_no} entirely? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/api/crm/slip-to-bill/${slip.id}`);
      toast.success(`Slip ${slip.slip_no} deleted`);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const removedCount = (slip.items || []).length - keep.size;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white border border-line p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-slip-modal">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display text-2xl text-maroon">Edit slip {slip.slip_no}</div>
          <button type="button" onClick={onClose}><X size={18}/></button>
        </div>
        <p className="text-xs text-ash mb-4">
          {slip.customer_name || "Walk-in"} · {slip.customer_phone}. Untick anything the customer
          decided against, and adjust the hallmarking (HUID) charge on any gold piece — the price
          follows it up or down. The slip will be flagged as <b className="text-amber-700">edited</b>.
        </p>

        <div className="border border-line divide-y divide-line/60 mb-4">
          {(slip.items || []).map((it, i) => {
            const on = keep.has(it.product_id);
            const goldPiece = it.huid_applicable !== false && huid[it.product_id] !== undefined;
            const newPrice = previewPrice(it);
            const moved = Math.abs(newPrice - Number(it.price || 0)) > 0.5;
            return (
              <div key={it.product_id || i} className={`p-3 ${on ? "" : "bg-red-50"}`} data-testid={`edit-slip-item-${i}`}>
                <label className={`flex items-center gap-3 cursor-pointer ${on ? "" : "line-through text-ash"}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(it.product_id)}/>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{it.name || "Item"}</div>
                    <div className="text-[10px] text-ash">
                      {[it.label_no || it.barcode ? `Barcode ${it.label_no || it.barcode}` : null,
                        it.huid && `HUID ${it.huid}`, it.purity,
                        it.weight_grams != null && `${it.weight_grams}g`,
                        it.size && `Size ${it.size}`]
                        .filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div className={moved ? "text-ash line-through text-xs" : "font-semibold text-maroon"}>
                      {fmtINR(it.price || 0)}
                    </div>
                    {moved && <div className="font-semibold text-maroon">{fmtINR(newPrice)}</div>}
                  </div>
                </label>

                {on && goldPiece && (
                  <div className="mt-2 ml-7 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-ash">HUID charges</span>
                    <span className="text-[11px] text-ash">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="border border-line px-2 py-1 bg-pearl text-sm w-24"
                      value={huid[it.product_id]}
                      onChange={(e) => setHuidFor(it.product_id, e.target.value)}
                      data-testid={`slip-huid-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setHuidFor(it.product_id, "1250")}
                      className="text-[11px] text-maroon underline"
                    >
                      Reset to ₹1,250
                    </button>
                    {Number(huid[it.product_id]) !== Number(it.huid_charges ?? 1250) && (
                      <span className="text-[10px] text-amber-700">changed from {fmtINR(it.huid_charges ?? 1250)}</span>
                    )}
                  </div>
                )}
                {on && !goldPiece && (
                  <div className="mt-1 ml-7 text-[10px] text-ash">No hallmarking charge — {(it.metal || "this item").toLowerCase()} is not hallmarked.</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-pearl border border-line p-3 mb-3 text-sm flex items-center justify-between">
          <span className="text-ash text-xs">Slip total ({keep.size} item{keep.size === 1 ? "" : "s"})</span>
          <span>
            {Math.abs(newTotal - oldTotal) > 0.5 && (
              <span className="text-ash line-through text-xs mr-2">{fmtINR(oldTotal)}</span>
            )}
            <b className="text-maroon text-lg">{fmtINR(newTotal)}</b>
          </span>
        </div>

        <textarea
          rows={2} placeholder="Notes (optional)"
          className="w-full border border-line px-3 py-2 bg-pearl text-sm mb-3"
          value={notes} onChange={(e) => setNotes(e.target.value)}
        />

        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-ash">
            Keeping <b className="text-maroon">{keep.size}</b> of {(slip.items || []).length}
            {removedCount > 0 && <span className="text-amber-700"> · removing {removedCount}</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={del} disabled={busy} className="text-xs text-red-600 border border-red-300 px-3 py-2 disabled:opacity-50" data-testid="edit-slip-delete">
              Delete slip
            </button>
            <button onClick={save} disabled={busy || keep.size === 0} className="btn-maroon text-xs disabled:opacity-50" data-testid="edit-slip-save">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
